/**
 * DevPill - A consolidated developer pill UI rendered in a Shadow DOM portal.
 *
 * Surfaces controls and status from three external developer tools in a single
 * compact pill (draggable; docks to any of the four viewport edges):
 *
 * 1. **dev-proxy worktree switcher** - discovers dev-proxy via `/_service` API
 *    endpoint and fetches worktree options from `/api/routes/<service>`.
 * 2. **react-scan toolbar** - toggles visibility and reads render count from
 *    `#react-scan-root` shadow DOM (`#react-scan-toolbar`, `#react-scan-notifications`).
 * 3. **Next.js devtools indicator** - toggles visibility and detects error state via
 *    `nextjs-portal` shadow DOM (`#devtools-indicator` background color).
 *
 * WARNING: ReactScan and Next.js sections depend on internal DOM structure.
 * Changes to their shadow DOM selectors, element IDs, or markup structure may
 * break discovery or integration. The discovery loop polls for up to 10 seconds
 * on mount to find each tool's DOM elements.
 *
 * The component renders inside a Shadow DOM to achieve full style isolation from
 * the host page. It is a no-op when neither `NODE_ENV === 'development'` nor
 * `NEXT_PUBLIC_VERCEL_ENV` is `'preview'` or `'staging'`.
 */
'use client';

import { c as _c } from "react/compiler-runtime";
import { buildTitlePrefix } from '@fpo3/ts-commons-core/dev-proxy-url';
import { Minimize2, Wrench } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createDevProxyAdapter, createNextjsAdapter, createReactScanAdapter, type DevProxyAdapter, type NextjsAdapter, type PillPosition, type ReactScanAdapter } from './adapters';
import { CustomSection } from './dev-pill-sections/custom-section';
import { NextjsSection, useNextjsSection } from './dev-pill-sections/nextjs-section';
import { ScanSection, useScanSection } from './dev-pill-sections/scan-section';
import { useWorktreeSection, WorktreeSection } from './dev-pill-sections/worktree-section';
import { STYLES } from './dev-pill-styles';
import { type DevPillAppId, getExpandedStore, type PillOffset } from './pill-positioning';
import { useDraggablePill } from './use-draggable-pill';

/** Descriptor for an app-specific section injected into the developer pill alongside the built-in worktree, scan, and Next.js sections. */
export interface CustomPillSection {
  id: string;
  icon: React.ReactNode;
  /** Accessible name for the section's icon button. Set this when `icon` is
   *  icon-only (no text) so the button has a non-empty accessible name. */
  ariaLabel?: string | undefined;
  /** Popup content. Pass a render function `(onClose) => ReactNode` to receive a close callback. */
  content: React.ReactNode | ((onClose: () => void) => React.ReactNode);
  /** When false, the popup will NOT close on outside clicks — only via the onClose callback.
   *  Useful for panels where the user needs to interact with the page (e.g., bug reporting).
   *  Defaults to true. */
  dismissOnClickOutside?: boolean | undefined;
  /** Called with an imperative `open()` function when the section mounts.
   *  Use this to programmatically open the popup from external code. */
  onRegisterOpen?: ((openFn: () => void) => void) | undefined;
  /** When provided, replaces the default popup toggle behavior on icon click.
   *  The popup will NOT open; the callback handles the click entirely. */
  onClick?: (() => void) | undefined;
}

// ── Discovery Hook ──

/** Holds the ready/position state resolved after the async DOM discovery pass for each built-in developer tool adapter. */
interface DiscoveryState {
  proxyReady: boolean;
  scanReady: boolean;
  nextjsReady: boolean;
  nextjsPosition: PillPosition;
}

/** Discovers dev-proxy via an async adapter fetch and polls the DOM for react-scan and Next.js adapter elements for up to 10 s on mount, resolving their initial visibility and position state. */
function useDiscovery(proxyAdapter: DevProxyAdapter, scanAdapter: ReactScanAdapter, nextjsAdapter: NextjsAdapter): DiscoveryState {
  const [proxyReady, setProxyReady] = useState(false);
  const [scanReady, setScanReady] = useState(false);
  const [nextjsReady, setNextjsReady] = useState(false);
  const [nextjsPosition, setNextjsPosition] = useState<PillPosition>('hidden');
  useEffect(() => {
    const state = {
      cancelled: false
    };

    // Async proxy discovery — single fetch, not polled
    proxyAdapter.discover().then(ready => {
      if (!state.cancelled && ready) {
        setProxyReady(true);
      }
    }).catch(() => {
      // discovery failure is graceful — proxyReady stays false
    });

    // Synchronous DOM polling for scan and nextjs (unchanged behavior)
    let attempts = 0;
    const found = {
      scan: false,
      nextjs: false
    };

    /** Runs a single discovery tick and reschedules itself every 100 ms until all tools are found or 100 attempts are exhausted. */
    function discover() {
      if (state.cancelled) return;
      if (++attempts > 100) return;
      if (!found.scan && scanAdapter.discover()) {
        found.scan = true;
        scanAdapter.hide();
        setScanReady(true);
      }
      if (!found.nextjs && nextjsAdapter.discover()) {
        found.nextjs = true;
        setNextjsPosition(nextjsAdapter.getPosition());
        nextjsAdapter.hide();
        setNextjsReady(true);
      }
      if (!found.scan || !found.nextjs) {
        setTimeout(discover, 100);
      }
    }
    discover();
    return () => {
      state.cancelled = true;
    };
  }, [proxyAdapter, scanAdapter, nextjsAdapter]);
  return {
    proxyReady,
    scanReady,
    nextjsReady,
    nextjsPosition
  };
}

// ── PillUI Compositor ──

/** Layout compositor: arranges the discovered sections into a draggable pill with expand/collapse toggle and localStorage persistence keyed by appId. */
function PillUI({
  appId,
  customSections,
  position: positionOverride,
  skipWorktreeNameInTitlePrefix,
  offset
}: {
  appId: DevPillAppId;
  customSections?: CustomPillSection[] | undefined;
  /** The `'hidden'` case is short-circuited by `DevPillImpl` before
   *  rendering `PillUI`, so this type narrows to the visible positions. */
  position?: Exclude<PillPosition, 'hidden'> | undefined;
  skipWorktreeNameInTitlePrefix?: boolean | undefined;
  offset?: PillOffset | undefined;
}): React.ReactNode {
  const proxyAdapter = useMemo(() => createDevProxyAdapter(), []);
  const scanAdapter = useMemo(() => createReactScanAdapter(), []);
  const nextjsAdapter = useMemo(() => createNextjsAdapter(), []);
  const {
    proxyReady,
    scanReady,
    nextjsReady,
    nextjsPosition: detectedPosition
  } = useDiscovery(proxyAdapter, scanAdapter, nextjsAdapter);

  // Caller-specified position takes precedence over auto-detection.
  // Auto-detection via getBoundingClientRect is unreliable during early discovery
  // when the Next.js indicator DOM element may not be positioned yet.
  const position: Exclude<PillPosition, 'hidden'> = positionOverride ?? (detectedPosition === 'hidden' ? 'bottom-right' : detectedPosition);
  const worktree = useWorktreeSection(proxyAdapter, proxyReady);
  const scan = useScanSection(scanAdapter, scanReady);
  const nextjs = useNextjsSection(nextjsAdapter, nextjsReady);
  const worktreeWrapRef = useRef<HTMLDivElement>(null);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  // Separate refs for the visible inner box. `.pill-container` is
  // `display: contents`, so its rect is zero — `useDraggablePill` measures
  // the visible `.pill` (desktop) or `.pill-fab` (mobile collapsed) instead.
  const [pillEl, setPillEl] = useState<HTMLDivElement | null>(null);
  const [fabEl, setFabEl] = useState<HTMLButtonElement | null>(null);
  const hasCustomSections = (customSections?.length ?? 0) > 0;

  // Title prefixing when running behind dev-proxy.
  // When skipWorktreeNameInTitlePrefix is true, the [worktree] portion is skipped but
  // the machine indicator (💻 on local machines; nothing on cloud nodes)
  // is still applied.
  const prefixRef = useRef<string | null>(null);
  useEffect(() => {
    if (!proxyReady) return;
    const wt = proxyAdapter.getCurrentWorktree();
    if (!wt) return;
    const isCloud = proxyAdapter.isCloudNode();

    // When skipWorktreeNameInTitlePrefix, only inject the machine indicator emoji

    // Strip any previous prefix before applying the new one (prevents stacking
    // when cloud-node status or worktree name changes between effect runs).
    const oldPrefix = prefixRef.current;
    if (oldPrefix && document.title.startsWith(oldPrefix)) {
      document.title = document.title.slice(oldPrefix.length);
    }
    const prefix = buildTitlePrefix(wt, isCloud, {
      skipWorktreeName: !!skipWorktreeNameInTitlePrefix
    });
    prefixRef.current = prefix || null;

    // No prefix to apply (cloud-node + skipWorktreeNameInTitlePrefix yields ''):
    // skip scheduling a no-op poller. The oldPrefix-stripping above still ran.
    if (!prefix) return;

    /** Prepends the computed worktree prefix to the document title if it is not already present. */
    function updateTitle() {
      if (!document.title.startsWith(prefix)) {
        document.title = prefix + document.title;
      }
    }
    updateTitle();
    const id = setInterval(updateTitle, 500);
    return () => {
      clearInterval(id);
      // Strip prefix on cleanup so the next effect run starts clean
      if (prefixRef.current && document.title.startsWith(prefixRef.current)) {
        document.title = document.title.slice(prefixRef.current.length);
      }
    };
  }, [skipWorktreeNameInTitlePrefix, proxyReady, proxyAdapter]);

  // All drag state — position, listeners, ResizeObserver, click suppression
  // — lives in the `useDraggablePill` hook. The hook owns its own cleanup;
  // the component reads only the snapped `edgePosition` and the `data-*`
  // attributes to apply to the container.
  const {
    containerAttrs
  } = useDraggablePill({
    containerEl,
    pillEl,
    fabEl,
    position,
    nextjsVisible: nextjs.visible,
    offset
  });

  // Collapsed/expanded state persists per app (default collapsed) and stays in
  // sync across tabs of the same app via the store's `useSyncExternalStore`
  // hook. Toggling writes through the store; the hook re-renders on change.
  const expandedStore = useMemo(() => getExpandedStore(appId), [appId]);
  const expanded = expandedStore.useValue();

  // Note: the `position === 'hidden'` short-circuit lives in `DevPillImpl`
  // BEFORE this component is rendered, so `useDraggablePill` above is
  // never invoked with a hidden position. Per Phase 3 of the round-3
  // enactment plan (FPO-NA review-9016e0cc-83b issue #4).

  // Without custom sections, wait for at least one tool to be available
  if (!hasCustomSections && !proxyReady && !scanReady && !scan.disabled && !nextjsReady) return null;

  // Collect visible sections, then interleave dividers between them
  const parts: React.ReactNode[] = [];
  if (hasCustomSections) {
    for (const cs of customSections!) {
      parts.push(<CustomSection key={cs.id} section={cs} />);
    }
  }
  if (proxyReady) {
    parts.push(<WorktreeSection key="worktree" section={worktree} wrapRef={worktreeWrapRef} />);
  }
  if (scanReady || scan.disabled) {
    parts.push(<ScanSection key="scan" section={scan} />);
  }
  if (nextjsReady) {
    parts.push(<NextjsSection key="nextjs" section={nextjs} />);
  }
  const sections: React.ReactNode[] = [];
  for (let i = 0; i < parts.length; i++) {
    if (i > 0) sections.push(<div key={`divider-${i}`} className="divider" />);
    sections.push(parts[i]);
  }
  return <div className={`pill-container${expanded ? ' expanded' : ''}`} ref={setContainerEl} {...containerAttrs}>
      {/* Collapsed FAB circle */}
      <button type="button" className="pill-fab" aria-label="Open dev pill" onClick={() => expandedStore.set(true)} ref={setFabEl}>
        <Wrench size={14} />
      </button>
      {/* Full pill bar (toggled by collapse button) */}
      <div className="pill" ref={setPillEl}>
        <button type="button" className="pill-collapse" aria-label="Collapse dev pill" onClick={() => expandedStore.set(false)}>
          <Minimize2 size={14} />
        </button>
        {sections}
      </div>
    </div>;
}

/** Props accepted by the public `DevPill` component, controlling identity, placement, and optional custom sections. */
interface DevPillProps {
  /** Which app is rendering the pill. Scopes the per-app collapsed/expanded
   *  persistence key (`devpill:expanded:<appId>:v1`). Required so a new
   *  consumer can't silently fall back to a shared key. */
  appId: DevPillAppId;
  customSections?: CustomPillSection[] | undefined;
  /** Initial position seed used when no dragged position has been persisted.
   *  Prevents the pill from jumping when the Next.js indicator DOM hasn't
   *  been positioned yet at discovery time. */
  position?: PillPosition | undefined;
  /** Additional pixel offset added to the computed host style. Use to clear
   *  app-specific fixed-position chrome (e.g. mobile chat input bars). */
  offset?: PillOffset | undefined;
  /** When true, skip the `[worktree]` title prefix. The machine indicator
   *  (💻 on local machines; nothing on cloud nodes) is still applied. Use
   *  for apps that manage their own worktree display (e.g. wt-web). */
  skipWorktreeNameInTitlePrefix?: boolean | undefined;
  /** When true, render the pill even in production (`NODE_ENV=production`).
   *  Used by wt-web which runs as a production build but still needs the
   *  worktree switcher and bug reporter. */
  forceShow?: boolean | undefined;
}

/** Mounts a Shadow DOM host on `<body>`, injects scoped styles, and renders PillUI inside the shadow root to prevent CSS leakage from the host page. */
function DevPillImpl(t0) {
  const $ = _c(11);
  const {
    appId,
    customSections,
    position,
    offset,
    skipWorktreeNameInTitlePrefix
  } = t0;
  const [mountTarget, setMountTarget] = useState(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const host = document.createElement("div");
      host.setAttribute("data-dev-pill", "true");
      host.addEventListener("pointerdown", _temp);
      host.addEventListener("focusin", _temp2);
      document.body.appendChild(host);
      const shadow = host.attachShadow({
        mode: "open"
      });
      const style = document.createElement("style");
      style.textContent = STYLES;
      shadow.appendChild(style);
      const container = document.createElement("div");
      shadow.appendChild(container);
      setMountTarget(container);
      return () => {
        host.remove();
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  if (!mountTarget) {
    return null;
  }
  let t3;
  if ($[2] !== appId || $[3] !== customSections || $[4] !== offset || $[5] !== position || $[6] !== skipWorktreeNameInTitlePrefix) {
    t3 = <PillUI appId={appId} customSections={customSections} position={position} offset={offset} skipWorktreeNameInTitlePrefix={skipWorktreeNameInTitlePrefix} />;
    $[2] = appId;
    $[3] = customSections;
    $[4] = offset;
    $[5] = position;
    $[6] = skipWorktreeNameInTitlePrefix;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== mountTarget || $[9] !== t3) {
    t4 = createPortal(t3, mountTarget);
    $[8] = mountTarget;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}

/**
 * Returns `true` when the current environment is a non-production environment
 * where developer tooling should be shown:
 * - Local development: `NODE_ENV === 'development'`
 * - Vercel preview or staging: `NEXT_PUBLIC_VERCEL_ENV` is `'preview'` or `'staging'`
 *
 * Accepts optional parameters so the function can be tested without mocking
 * `process.env`.
 *
 * NOTE: famfam/components/layout/authenticated-layout.tsx duplicates this
 * condition inline (must be literal process.env.* for Next.js tree-shaking).
 * Keep both in sync when changing environments.
 */
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp(e) {
  return e.stopPropagation();
}
export function isNonProductionEnv(nodeEnv: string | undefined = process.env['NODE_ENV'], vercelEnv: string | undefined = process.env['NEXT_PUBLIC_VERCEL_ENV']): boolean {
  return nodeEnv === 'development' || vercelEnv === 'preview' || vercelEnv === 'staging';
}

/**
 * Consolidated developer pill aggregating dev-proxy worktree switcher,
 * react-scan toolbar controls, and Next.js build indicator.
 *
 * Requires `appId` (`'famfam' | 'wt-web' | 'agent-history'`) to scope the
 * per-app collapsed/expanded persistence key. The pill defaults to collapsed
 * on first load and remembers the choice per app across reloads.
 *
 * Accepts optional `customSections` to add app-specific sections with
 * popup menus styled like the worktree popup.
 *
 * Pass `position` to seed the initial pill placement and avoid jumps caused by
 * unreliable auto-detection during early discovery. A persisted dragged
 * position takes precedence on later mounts.
 *
 * Pass `skipWorktreeNameInTitlePrefix` to suppress the automatic `[worktree]` title
 * prefix. The machine indicator (💻 on local machines; nothing on cloud
 * nodes) is still applied.
 * Use for apps that manage their own worktree display (e.g. wt-web).
 *
 * Renders in a Shadow DOM portal for full style isolation. Returns `null`
 * (no-op) when `NODE_ENV` is not `'development'` and `NEXT_PUBLIC_VERCEL_ENV`
 * is not `'preview'` or `'staging'` — unless `forceShow` is true.
 */
export function DevPill(t0) {
  const $ = _c(6);
  const {
    appId,
    customSections,
    position,
    offset,
    skipWorktreeNameInTitlePrefix,
    forceShow
  } = t0;
  if (!forceShow && !isNonProductionEnv()) {
    return null;
  }
  if (position === "hidden") {
    return null;
  }
  let t1;
  if ($[0] !== appId || $[1] !== customSections || $[2] !== offset || $[3] !== position || $[4] !== skipWorktreeNameInTitlePrefix) {
    t1 = <DevPillImpl appId={appId} customSections={customSections} position={position} offset={offset} skipWorktreeNameInTitlePrefix={skipWorktreeNameInTitlePrefix} />;
    $[0] = appId;
    $[1] = customSections;
    $[2] = offset;
    $[3] = position;
    $[4] = skipWorktreeNameInTitlePrefix;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}